import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTimelineImageUpload } from '@/hooks/useTimelineImageUpload';
import { PrivacyLevel, TimelineSection } from '@/types';
import ChatMessage from '@/components/memory/chat/ChatMessage';
import QuickReplyButtons from '@/components/memory/chat/QuickReplyButtons';
import ChatInput from '@/components/memory/chat/ChatInput';
import RollingDatePicker from '@/components/memory/chat/RollingDatePicker';
import PhotoUploadChatWrapper from '@/components/memory/chat/PhotoUploadChatWrapper';
import TimelineSidebar from './TimelineSidebar';
import TimelineSidebarBottomNav from './TimelineSidebarBottomNav';
import { getSectionDisplayMessage, findSectionForYear } from '@/utils/timelineSectionHelpers';

interface TimelineSidebarChatProps {
  isOpen: boolean;
  onClose: () => void;
  onMemoryCreated?: (sectionId: string) => void;
  className?: string;
  targetUserId?: string;
  targetUserProfile?: any;
  timelineSections?: TimelineSection[];
  sectionsLoading?: boolean;
  timelineError?: boolean;
  preselectedSectionId?: string;
}

type ConversationStep = 'lifePhase' | 'description' | 'dateSpecific' | 'photos' | 'privacy' | 'confirm';

interface Message {
  id: string;
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface MemoryData {
  title: string;
  content: string;
  photos: File[];
  uploadedPhotoUrls: string[];
  privacy: PrivacyLevel;
  sectionId: string;
  year: string;
  month: string;
  day: string;
  season?: string;
}

const TimelineSidebarChat = ({
  isOpen,
  onClose,
  onMemoryCreated,
  className,
  targetUserId,
  targetUserProfile,
  timelineSections = [],
  sectionsLoading = false,
  timelineError = false,
  preselectedSectionId
}: TimelineSidebarChatProps) => {
  const { user } = useAuth();
  const { t } = useTranslation(['memory', 'common', 'timeline']);
  const { uploadImages, isUploading, currentQualitySettings } = useTimelineImageUpload(targetUserId || user?.id || '');
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState<ConversationStep>('photos');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentLifePhase, setCurrentLifePhase] = useState<TimelineSection | null>(null);
  const [memoryData, setMemoryData] = useState<MemoryData>({
    title: '',
    content: '',
    photos: [],
    uploadedPhotoUrls: [],
    privacy: PrivacyLevel.FRIENDS,
    sectionId: '',
    year: '',
    month: '',
    day: ''
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageIdCounter = useRef(0);

  // Determine if this is the user's own timeline
  const isOwnTimeline = !targetUserId || user?.id === targetUserId;
  const timelineOwnerName = targetUserProfile?.first_name || targetUserProfile?.username || 'Timeline Owner';

  // Debug what's being passed to the sidebar
  useEffect(() => {
    console.log('📱 TimelineSidebarChat - Props received:', {
      isOpen,
      sectionsLoading,
      timelineError,
      timelineSectionsCount: timelineSections.length,
      timelineSections: timelineSections.map(s => ({ id: s.id, name: s.name })),
      targetUserId,
      targetUserProfile: targetUserProfile ? { id: targetUserProfile.id, username: targetUserProfile.username } : null
    });
  }, [isOpen, sectionsLoading, timelineError, timelineSections, targetUserId, targetUserProfile]);

  // Generate unique message ID
  const generateMessageId = () => {
    messageIdCounter.current += 1;
    return `${Date.now()}-${messageIdCounter.current}`;
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Only start chat when sidebar is open, data is loaded, no error, and we have sections
    if (isOpen && !sectionsLoading && !timelineError && timelineSections.length > 0 && messages.length === 0) {
      console.log('📱 TimelineSidebarChat - Starting chat conversation');
      
      // Start with photos first
      addBotMessage(t('memory:chat.askPhotos'));
    }
  }, [isOpen, sectionsLoading, timelineError, timelineSections.length, messages.length]);

  const addBotMessage = (content: string) => {
    const message: Message = {
      id: generateMessageId(),
      type: 'bot',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
  };

  const addUserMessage = (content: string) => {
    const message: Message = {
      id: generateMessageId(),
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
  };

  // Calculate middle year of a section
  const getSectionMiddleYear = (sectionId: string): string => {
    if (!targetUserProfile?.birthdate || !sectionId) return '';
    
    const section = timelineSections.find(s => s.id === sectionId);
    if (!section) return '';
    
    const birthYear = new Date(targetUserProfile.birthdate).getFullYear();
    const middleAge = Math.floor((section.age_from + section.age_to) / 2);
    const middleYear = birthYear + middleAge;
    
    console.log('🎯 getSectionMiddleYear:', {
      sectionName: section.name,
      ageFrom: section.age_from,
      ageTo: section.age_to,
      middleAge,
      birthYear,
      middleYear
    });
    
    return middleYear.toString();
  };

  const handleSectionChange = (newSection: TimelineSection) => {
    console.log('🔄 TimelineSidebarChat - handleSectionChange called:', {
      currentSectionId: memoryData.sectionId,
      newSectionId: newSection.id,
      newSectionName: newSection.name
    });

    // Update the existing user message that selected the life phase
    setMessages(prev => {
      const updatedMessages = [...prev];
      // Find the last user message that corresponds to life phase selection
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].type === 'user' && 
            // Check if this message was likely a life phase selection by looking for section names
            timelineSections.some(section => updatedMessages[i].content.includes(section.name))) {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: newSection.name
          };
          break;
        }
      }
      return updatedMessages;
    });

    setMemoryData(prev => ({ ...prev, sectionId: newSection.id }));
    setCurrentLifePhase(newSection);
  };

  const handleYearChange = (year: string) => {
    if (!year || !timelineSections.length || !targetUserProfile?.birthdate) return;
    
    const newSection = findSectionForYear(year, timelineSections, targetUserProfile.birthdate);
    if (newSection) {
      setCurrentLifePhase(newSection);
    }
  };

  const handleTodaySelection = () => {
    if (!targetUserProfile?.birthdate || !timelineSections.length) return;
    
    const today = new Date();
    const currentYear = today.getFullYear().toString();
    
    // Find the current life phase based on today's date
    const currentSection = findSectionForYear(currentYear, timelineSections, targetUserProfile.birthdate);
    
    if (currentSection) {
      console.log('🎯 Today selection - found current section:', currentSection.name);
      
      // Set memory data with current section and today's date
      setMemoryData(prev => ({ 
        ...prev, 
        sectionId: currentSection.id,
        year: currentYear,
        month: (today.getMonth() + 1).toString(),
        day: today.getDate().toString()
      }));
      
      // Add user message and progress to next step
      addUserMessage(`${currentSection.name} (Today)`);
      setTimeout(() => {
        addBotMessage(t('memory:chat.describeMemory'));
        setCurrentStep('description');
      }, 500);
    }
  };

  const handleStepProgress = async (stepData: any) => {
    console.log('🚀 TimelineSidebarChat - handleStepProgress called:', {
      currentStep,
      stepData: typeof stepData === 'object' ? { ...stepData, photos: stepData.photos?.length || 0 } : stepData
    });

    switch (currentStep) {
      case 'photos':
        console.log('📸 TimelineSidebarChat - Photos step received:', {
          stepData,
          hasPhotos: stepData.photos && stepData.photos.length > 0,
          photosCount: stepData.photos?.length || 0,
          photosDetails: stepData.photos?.map(f => ({ name: f.name, size: f.size, type: f.type })) || []
        });

        if (stepData.photos && stepData.photos.length > 0) {
          setMemoryData(prev => {
            const newData = { ...prev, photos: stepData.photos };
            console.log('📸 TimelineSidebarChat - Updated memoryData with photos:', {
              photosCount: newData.photos.length,
              photosDetails: newData.photos.map(f => ({ name: f.name, size: f.size, type: f.type }))
            });
            return newData;
          });
          addUserMessage(t('memory:chat.photosAdded', { count: stepData.photos.length }));
        } else {
          console.log('📸 TimelineSidebarChat - No photos received');
          addUserMessage(t('memory:chat.noPhotos'));
        }
        
        // Check if preselected section exists
        if (preselectedSectionId) {
          const preselectedSection = timelineSections.find(s => s.id === preselectedSectionId);
          if (preselectedSection) {
            console.log('📱 TimelineSidebarChat - Pre-selecting section:', preselectedSection.name);
            
            // Set the memory data with the preselected section
            setMemoryData(prev => ({ ...prev, sectionId: preselectedSectionId }));
            setCurrentLifePhase(preselectedSection);
            
            // Add user message showing the selected life phase
            addUserMessage(preselectedSection.name);
            
            // Go to date specific step
            setTimeout(() => {
              addBotMessage(t('memory:chat.datePrompt'));
              setCurrentStep('dateSpecific');
            }, 500);
          } else {
            // Fallback to normal flow if section not found
            setTimeout(() => {
              addBotMessage(t('memory:chat.askLifePhase'));
              setCurrentStep('lifePhase');
            }, 500);
          }
        } else {
          // Normal flow - ask for life phase
          setTimeout(() => {
            addBotMessage(t('memory:chat.askLifePhase'));
            setCurrentStep('lifePhase');
          }, 500);
        }
        break;

      case 'lifePhase':
        setMemoryData(prev => ({ ...prev, sectionId: stepData.id }));
        addUserMessage(stepData.name);
        setTimeout(() => {
          addBotMessage(t('memory:chat.datePrompt'));
          setCurrentStep('dateSpecific');
        }, 500);
        break;

      case 'description':
        const description = stepData || '';
        // Generate title from first few words of description
        const title = description.split(' ').slice(0, 4).join(' ') || 'Untitled Memory';
        setMemoryData(prev => ({
          ...prev,
          title: title,
          content: description
        }));
        addUserMessage(description || t('memory:chat.skipDescription'));
        setTimeout(() => {
          addBotMessage(t('memory:chat.askPrivacy'));
          setCurrentStep('privacy');
        }, 500);
        break;

      case 'dateSpecific':
        setMemoryData(prev => ({
          ...prev,
          year: stepData.year || '',
          month: stepData.month || '',
          day: stepData.day || '',
          season: stepData.season
        }));
        addUserMessage(stepData.label);
        setTimeout(() => {
          addBotMessage(t('memory:chat.describeMemory'));
          setCurrentStep('description');
        }, 500);
        break;

      case 'privacy':
        setMemoryData(prev => ({
          ...prev,
          privacy: stepData.value
        }));
        addUserMessage(stepData.label);
        setTimeout(() => {
          addBotMessage(t('memory:chat.confirmMemory'));
          setCurrentStep('confirm');
        }, 500);
        break;

      case 'confirm':
        // This case should not be reached as confirm is handled by handleConfirm
        break;

      default:
        break;
    }
  };

  const handleTextSubmit = () => {
    if (!inputValue.trim()) return;
    if (currentStep === 'description') {
      handleStepProgress(inputValue);
    }
    setInputValue('');
  };

  const handleConfirm = async () => {
    console.log('💾 TimelineSidebarChat - handleConfirm called with memoryData:', {
      title: memoryData.title,
      content: memoryData.content,
      sectionId: memoryData.sectionId,
      photosCount: memoryData.photos.length,
      photosDetails: memoryData.photos.map(f => ({ name: f.name, size: f.size, type: f.type })),
      privacy: memoryData.privacy,
      targetUserId: targetUserId
    });

    if (!user) {
      toast.error(t('memory:messages.loginRequired'));
      return;
    }
    
    if (!memoryData.content.trim() || !memoryData.sectionId) {
      toast.error(t('memory:messages.fillRequired'));
      return;
    }
    
    if (!targetUserId) {
      toast.error('Target user not found');
      return;
    }

    try {
      // Upload images first if any and get the URLs directly
      let uploadedUrls: string[] = [];
      if (memoryData.photos.length > 0) {
        console.log('📤 TimelineSidebarChat - Starting image upload for photos:', {
          count: memoryData.photos.length,
          details: memoryData.photos.map(f => ({ name: f.name, size: f.size, type: f.type }))
        });
        
        uploadedUrls = await uploadImages(memoryData.photos);
        
        console.log('📤 TimelineSidebarChat - Image upload completed:', {
          uploadedUrls,
          urlsCount: uploadedUrls.length
        });
      } else {
        console.log('📤 TimelineSidebarChat - No photos to upload');
      }

      let dateObj = null;
      let formattedDate = null;
      let yearNum = memoryData.year ? parseInt(memoryData.year) : null;
      let monthNum = memoryData.month ? parseInt(memoryData.month) : null;
      let dayNum = memoryData.day ? parseInt(memoryData.day) : null;
      
      if (yearNum) {
        monthNum = monthNum || 1;
        dayNum = dayNum || 1;
        dateObj = new Date(yearNum, monthNum - 1, dayNum);
        formattedDate = format(dateObj, "yyyy-MM-dd");
      }

      console.log('💾 TimelineSidebarChat - Inserting memory to database:', {
        title: memoryData.title,
        sectionId: memoryData.sectionId,
        photosToInsert: uploadedUrls.length > 0 ? uploadedUrls : null,
        formattedDate,
        privacy: memoryData.privacy,
        userId: targetUserId, // Timeline owner
        creatorId: user.id // Current user creating the entry
      });

      const { error } = await supabase
        .from('profile_timeline_entries')
        .insert({
          user_id: targetUserId, // Timeline owner's ID
          creator_id: user.id, // Current user's ID
          profile_timeline_section_id: memoryData.sectionId,
          title: memoryData.title,
          content: memoryData.content || null,
          date: formattedDate,
          year: yearNum,
          month: monthNum,
          day: dayNum,
          privacy_level: memoryData.privacy,
          photos: uploadedUrls.length > 0 ? uploadedUrls : null
        });
        
      if (error) throw error;
      
      console.log('✅ TimelineSidebarChat - Memory created successfully');
      toast.success(t('memory:messages.memoryCreated'));
      handleClose(memoryData.sectionId);
    } catch (error: any) {
      console.error('❌ TimelineSidebarChat - Error creating memory:', error);
      toast.error(t('memory:messages.memoryError', { error: error.message }));
    }
  };

  const resetChat = () => {
    setCurrentStep('lifePhase');
    setMessages([]);
    setInputValue('');
    setMemoryData({
      title: '',
      content: '',
      photos: [],
      uploadedPhotoUrls: [],
      privacy: PrivacyLevel.FRIENDS,
      sectionId: '',
      year: '',
      month: '',
      day: ''
    });
    messageIdCounter.current = 0;
  };

  const handleClose = (createdSectionId?: string) => {
    resetChat();
    if (createdSectionId && onMemoryCreated) {
      onMemoryCreated(createdSectionId);
    }
    onClose();
  };

  const handleBack = () => {
    handleClose();
  };

  const handleSparkles = () => {
    toast.info("This feature will be revealed soon");
  };

  const handleMic = () => {
    toast.info("Voice input feature coming soon");
  };


  const renderChatInput = () => {
    // Show loading state while sections are being loaded
    if (sectionsLoading) {
      console.log('📱 TimelineSidebarChat - Showing loading state');
      return (
        <div className="flex justify-center p-4">
          <div className="text-gray-500">Loading timeline sections...</div>
        </div>
      );
    }

    // Show error state if timeline data couldn't be loaded
    if (timelineError) {
      console.log('📱 TimelineSidebarChat - Showing error state');
      return (
        <div className="flex flex-col items-center p-4 text-center">
          <div className="text-red-500 mb-2">Unable to load timeline data</div>
        </div>
      );
    }

    // Show "no sections" state if we have no sections but no error
    if (!sectionsLoading && !timelineError && timelineSections.length === 0) {
      console.log('📱 TimelineSidebarChat - Showing no sections state');
      return (
        <div className="flex flex-col items-center p-4 text-center">
          <div className="text-gray-500 mb-2">No timeline sections available</div>
        </div>
      );
    }

    switch (currentStep) {
      case 'description':
        return (
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSubmit={handleTextSubmit}
            placeholder={t('memory:chat.descriptionPlaceholder')}
            isCreatingForOtherUser={!isOwnTimeline}
            timelineOwnerName={timelineOwnerName}
            isOwnTimeline={isOwnTimeline}
          />
        );

      case 'lifePhase':
        // Get user's birth year for year range calculation
        const birthYear = targetUserProfile?.birthdate ? new Date(targetUserProfile.birthdate).getFullYear() : new Date().getFullYear() - 25;
        
        const lifePhasesOptions = timelineSections?.map(section => {
          const yearFrom = birthYear + section.age_from;
          const yearTo = birthYear + section.age_to;
          
          return {
            id: section.id,
            label: section.name,
            value: section,
            ageRange: section.age_from !== undefined && section.age_to !== undefined ? `${section.age_from}-${section.age_to} Jahre` : undefined,
            yearRange: `${yearFrom}-${yearTo}`
          };
        }) || [];
        
        return (
          <div className="flex flex-col h-full">
            {/* Scrollable life phases list */}
            <div className="flex-1 p-4 pb-2">
              <QuickReplyButtons
                options={lifePhasesOptions}
                onSelect={handleStepProgress}
              />
            </div>
            
            {/* Fixed "Today" button at bottom */}
            <div className="flex-shrink-0 p-4 pt-2 border-t border-white/20">
              <button
                onClick={handleTodaySelection}
                className="w-full bg-uplivion-blue hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
              >
                {t('memory:chat.today')}
              </button>
            </div>
          </div>
        );

      case 'dateSpecific':
        const middleYear = getSectionMiddleYear(memoryData.sectionId);
        const initialSection = timelineSections.find(s => s.id === memoryData.sectionId);
        
        // Set initial life phase if not already set
        if (!currentLifePhase && initialSection) {
          setCurrentLifePhase(initialSection);
        }
        
        // Use today's date if it was set from "Today" selection, otherwise use middle year
        const initialValues = {
          year: memoryData.year || middleYear,
          month: memoryData.month || '',
          day: memoryData.day || ''
        };
        
        const handleSkipDate = () => {
          addUserMessage(t('memory:chat.skipDate'));
          setTimeout(() => {
            addBotMessage(t('memory:chat.describeMemory'));
            setCurrentStep('description');
          }, 500);
        };

        return (
          <div className="flex flex-col">
            <div className="p-4">
              <RollingDatePicker 
                onSelect={handleStepProgress} 
                onSectionChange={handleSectionChange}
                onYearChange={handleYearChange}
                showToday={false}
                initialValues={initialValues}
                timelineSections={timelineSections}
                currentSectionId={memoryData.sectionId}
                userBirthdate={targetUserProfile?.birthdate}
              />
            </div>
            <div className="flex flex-col gap-2 p-4 pt-0">
              {/* Continue button would be rendered by RollingDatePicker */}
              <button
                onClick={handleSkipDate}
                className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-3 rounded-full font-medium transition-colors"
              >
                {t('memory:chat.skip')}
              </button>
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="p-4">
            <PhotoUploadChatWrapper onPhotosSelected={handleStepProgress} />
          </div>
        );

      case 'privacy':
        return (
          <div className="p-4">
            <QuickReplyButtons
              options={[
                {
                  id: 'private',
                  label: isOwnTimeline 
                    ? t('timeline:privacy.justMe')
                    : t('timeline:privacy.meAndOwner', { name: timelineOwnerName }),
                  value: { 
                    value: PrivacyLevel.PRIVATE, 
                    label: isOwnTimeline 
                      ? t('timeline:privacy.justMe')
                      : t('timeline:privacy.meAndOwner', { name: timelineOwnerName })
                  }
                },
                ...(!isOwnTimeline ? [{
                  id: 'friends',
                  label: t('timeline:privacy.mutualFriends'),
                  value: { value: PrivacyLevel.FRIENDS, label: t('timeline:privacy.mutualFriends') }
                }] : []),
                {
                  id: 'public',
                  label: isOwnTimeline 
                    ? t('timeline:privacy.everyoneOnMyTimeline')
                    : t('timeline:privacy.everyoneOnTimeline', { name: timelineOwnerName }),
                  value: { 
                    value: PrivacyLevel.PUBLIC, 
                    label: isOwnTimeline 
                      ? t('timeline:privacy.everyoneOnMyTimeline')
                      : t('timeline:privacy.everyoneOnTimeline', { name: timelineOwnerName })
                  }
                }
              ]}
              onSelect={handleStepProgress}
            />
          </div>
        );

      case 'confirm':
        return (
          <div className="flex justify-center p-4">
            <button
              onClick={handleConfirm}
              disabled={isUploading}
              className="bg-uplivion-blue hover:bg-blue-700 text-white px-8 py-3 rounded-full font-medium transition-colors disabled:opacity-50"
            >
              {isUploading ? t('memory:buttons.saving') : t('memory:summary.submitButton')}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <TimelineSidebar
      isOpen={isOpen}
      onClose={handleClose}
      className={className}
      title={t('memory.createEntry')}
      headerContent={targetUserProfile?.display_name && !isOwnTimeline && (
        <p className="text-sm text-gray-600 mt-1">
          {t('memory.addingMemoryFor', { name: targetUserProfile.display_name })}
        </p>
      )}
      inputContent={renderChatInput()}
      footerContent={
        <TimelineSidebarBottomNav mode="create" />
      }
    >
      <div className="space-y-4 p-4">
        {/* Chat Messages */}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>
    </TimelineSidebar>
  );
};

export default TimelineSidebarChat;
